import { mapActions, mapGetters, mapState } from 'vuex';
import constants from '@/constants';
import { formMixin, validators } from '@/components/forms/mixin';
import getValidationMessage from '@/helpers/getValidationMessage';
import catchHtmlClickMixin from '@/helpers/catchHtmlClickMixin';
import loaderMixin from '@/mixins/loader';
import { MODALS } from '@/components/app/app-modals/app-modals';
import instantCreditService from '@/services/instant-credit';

export default {
  mixins: [
    formMixin,
    loaderMixin,
    catchHtmlClickMixin,
  ],
  props: {
    formType: {
      type: Number,
      required: false,
      default: constants.FORM_TYPE_IDV.INITIAL,
    },
    trigger: {
      type: String,
      required: false,
      default: '',
    }
  },
  validations() {
    if (this.isSsn4) {
      return {
        fields: {
          street: validators.required,
          city: validators.onlyCharacters,
          zipCode: validators.validateUSZipCode('country'),
          country: validators.required,
          nscbStatus: validators.validatenNscbStatus('country'),
          taxId: validators.validateSSN('country')
        },
      }
    } else {
      return {
        fields: {
          firstName: validators.onlyCharacters,
          lastName: validators.onlyCharacters,
          dateOfBirthDay: validators.required,
          dateOfBirthMonth: validators.required,
          dateOfBirthYear: validators.validateIsAdult('dateOfBirthMonth', 'dateOfBirthDay'),
          street: validators.required,
          city: validators.onlyCharacters,
          zipCode: validators.validateUSZipCode('country'),
          country: validators.required,
          nscbStatus: validators.validatenNscbStatus('country'),
          taxId: validators.validateSSN('country')
        },
      }
    }
  },
  data() {
    return {
      ready: false,
      years: constants.birthYears,
      months: constants.months,
      days: constants.days,
      countryID: null,
      disclaimer: '',
      countries: [],
      showNscbStatusExplanation: false,
      fields: {
        nscbStatus: false,
        firstName: '',
        lastName: '',
        dateOfBirthDay: '',
        dateOfBirthMonth: '',
        dateOfBirthYear: '',
        street: '',
        city: '',
        zipCode: '',
        country: '',
        taxId: '',
      },
    };
  },
  created() {
    this.getVerifyID().then((response) => {
      const data = response.data;
      this.disclaimer = data.disclaimer;
      this.countryID = data.countryID;
      this.countries = data.countryList.map(el => ({
        required: el.validatePostalCode,
        label: el.countryName,
        value: el.countryCode,
      }));
      if (this.countryID) {
        this.fields.country = this.countries.find(i => i.value === this.countryID) || '';
      } else if (this.countries.length === 1) {
        this.fields.country = this.countries[0];
      }

      if (this.isSsn4) {
        this.fields = {
          ...this.fields,
          street: this.userData.address,
          city: this.userData.city,
          zipCode: this.userData.postalCode,
        }
      } else if (this.formDataCache !== null) {
        this.fields = {
          ...this.fields,
          ...this.formDataCache,
        };
      }

      this.ready = true;
    });
  },
  computed: {
    ...mapGetters('app', ['isLayoutDesktop']),
    ...mapState('popup', ['formDataCache']),
    ...mapState('user', ['userData']),
    title() {
      return this.isSsn4 ? 'Identity Verification' : 'Personal Details';
    },
    submitLabel() {
      return this.isSsn4 ? 'Continue' : 'Next';
    },
    showNoSolicitingNotice() {
      return this.isNotUnitedStates;
    },
    isNotUnitedStates() {
      return this.fields.country &&
        this.fields.country.value &&
        this.fields.country.value !== 'US';
    },
    isSubmitButtonDisabled() {
      return this.invalidOrDisabled;
    },
    errorNotAdult() {
      const field = this.$v.fields.dateOfBirthYear;
      return (field.$error) ? getValidationMessage(field) : '';
    },
    zipCodeModifier() {
      return {
        column: true,
        'small-6': this.countries.length > 0,
        'small-12': this.countries.length === 0,
      };
    },
    countriesModifier() {
      return {
        column: true,
        'small-6': this.countries.length > 0,
      };
    },
    isSsn4() {
      return this.formType === constants.FORM_TYPE_IDV.SSN4IDV;
    },
  },
  methods: {
    ...mapActions('user', ['getVerifyID', 'verifyID', 'getAccountDetail', 'logout', 'setSsnInfoVerified']),
    ...mapActions('popup', [
      'setCurrentPopupWithDelay', 'cacheFormData', 'setCurrentPopup',
      'setCurrentPopupOptions',
    ]),
    ...mapActions('wallet', ['getBalance']),
    ...mapActions('notifications', ['setSuccessSideNotification']),
    onSubmit() {
      this.$gtm.trackEvent({
        event: 'eventTrackerTrigger',
        category: 'ID Verification',
        action: 'submit',
        label: 'ID Verify Next',
      });
      if (this.formIsValid() && this.formDisable()) {
        let payload = {};
        if (this.isSsn4) {
          payload = {
            address1: this.fields.street,
            city: this.fields.city,
            postalCode: this.fields.zipCode,
            countryID: this.fields.country.value,
            taxId: this.fields.taxId,
            formType: this.formType,
          };
        } else {
          payload = {
            firstName: this.fields.firstName,
            lastName: this.fields.lastName,
            birthDay: this.fields.dateOfBirthDay.value,
            birthMonth: this.fields.dateOfBirthMonth.value,
            birthYear: this.fields.dateOfBirthYear.value,
            address1: this.fields.street,
            city: this.fields.city,
            postalCode: this.fields.zipCode,
            countryID: this.fields.country.value,
            taxId: this.fields.taxId,
            formType: this.formType,
          };
        }
        if (this.showNoSolicitingNotice) {
          payload.nscbStatus = this.fields.nscbStatus ? 1 : 0;
        } else {
          payload.nscbStatus = -1;
        }
        this.verifyID(payload)
          .then((data) => {
            if (this.isNotUnitedStates) {
              window.location.href = data;
              return Promise.reject('');
            }
            return this.formEnable();
          })
          .then(this.getAccountDetail)
          .then(() => {
            this.$gtm.trackEvent({ event: 'PassIDV' });
            this.cacheFormData({});
            if (this.isSsn4) {
              this.setSsnInfoVerified();

              if (this.trigger === constants.FORM_TYPE_TRIGGERS.MODAL) {
                this.setCurrentPopupWithDelay({
                  name: MODALS.ADD_FUNDS,
                  options: {
                    checkInstantCredit: instantCreditService.checkIfCanRunAfterVerification(),
                  },
                });
              } else {
                this.setCurrentPopup();
              }
            } else {
              this.setCurrentPopupWithDelay({
                name: MODALS.ADD_FUNDS,
                options: {
                  checkInstantCredit: instantCreditService.checkIfCanRunAfterVerification(),
                },
              });
            }
            this.getBalance();
          })
          .catch((error) => {
            this.$gtm.trackEvent({ event: 'FailIDV' });
            this.formHandleErrors(error);
          });
      }
    },
  },
  beforeDestroy() {
    this.cacheFormData({ ...this.fields });
  },
};



// WEBPACK FOOTER //
// ./src/components/account/account-details/modal/account-details-verify.js